import { approxZero, EPSILON, type Vec2, vec } from '../math';

import { distanceToSegment } from './lines';
import { type Polygon, type Segment } from './types';

/**
 * Polygon algorithms.
 *
 * Vertices are an ordered ring; the closing edge from last back to first is implied and
 * never stored. Storing it would force every algorithm to handle the duplicate, and it
 * makes "how many vertices does this triangle have" ambiguous.
 */

/** Iterates the edges of the ring, including the implied closing edge. */
export function* edges(p: Polygon): Generator<Segment> {
  const n = p.length;
  if (n < 2) return;
  for (let i = 0; i < n; i++) {
    const a = p[i];
    const b = p[(i + 1) % n];
    if (a === undefined || b === undefined) continue;
    yield { a, b };
  }
}

/**
 * Twice the signed area — the shoelace sum.
 *
 * Kept separate from `area` because the *sign* is the polygon's orientation and the
 * doubled form avoids a division that several callers immediately undo.
 * Positive means counter-clockwise in world space (y-up).
 */
export function signedDoubleArea(p: Polygon): number {
  const n = p.length;
  if (n < 3) return 0;
  let sum = 0;
  let prev = p[n - 1];
  if (prev === undefined) return 0;
  for (let i = 0; i < n; i++) {
    const cur = p[i];
    if (cur === undefined) continue;
    sum += prev.x * cur.y - cur.x * prev.y;
    prev = cur;
  }
  return sum;
}

export function signedArea(p: Polygon): number {
  return signedDoubleArea(p) / 2;
}

export function area(p: Polygon): number {
  return Math.abs(signedArea(p));
}

/** `1` for counter-clockwise, `-1` for clockwise, `0` for a degenerate ring. */
export function orientation(p: Polygon, epsilon: number = EPSILON): -1 | 0 | 1 {
  const a = signedDoubleArea(p);
  if (approxZero(a, epsilon)) return 0;
  return a > 0 ? 1 : -1;
}

export function isCounterClockwise(p: Polygon, epsilon: number = EPSILON): boolean {
  return orientation(p, epsilon) > 0;
}

/** Returns the ring in counter-clockwise order, reversing a copy only when needed. */
export function toCounterClockwise(p: Polygon): Polygon {
  return orientation(p) < 0 ? [...p].reverse() : p;
}

export function perimeter(p: Polygon): number {
  let total = 0;
  for (const e of edges(p)) total += vec.distance(e.a, e.b);
  return total;
}

/**
 * Centroid of the *area*, not the average of the vertices.
 *
 * The vertex average is a different point for any polygon with unevenly spaced
 * vertices, and it is not what "centre of mass" means to a student. Falls back to the
 * vertex average only for degenerate rings, where the area centroid is undefined.
 */
export function centroid(p: Polygon): Vec2 {
  const n = p.length;
  if (n === 0) return vec.ZERO;
  if (n === 1) return p[0] ?? vec.ZERO;

  const doubleArea = signedDoubleArea(p);
  if (approxZero(doubleArea, EPSILON)) {
    let sx = 0;
    let sy = 0;
    for (const v of p) {
      sx += v.x;
      sy += v.y;
    }
    return { x: sx / n, y: sy / n };
  }

  let cx = 0;
  let cy = 0;
  let prev = p[n - 1];
  if (prev === undefined) return vec.ZERO;
  for (let i = 0; i < n; i++) {
    const cur = p[i];
    if (cur === undefined) continue;
    const cross = prev.x * cur.y - cur.x * prev.y;
    cx += (prev.x + cur.x) * cross;
    cy += (prev.y + cur.y) * cross;
    prev = cur;
  }
  const factor = 1 / (3 * doubleArea);
  return { x: cx * factor, y: cy * factor };
}

/**
 * Point-in-polygon by the crossing-number rule, correct for concave and self-
 * intersecting rings under the even-odd fill convention.
 *
 * Points on the boundary are reported as inside — a student clicking exactly on an edge
 * expects to select the shape.
 *
 * The half-open edge test (`(a.y > p.y) !== (b.y > p.y)`) is what makes a vertex at
 * exactly the ray's height count once rather than zero or twice.
 */
export function containsPoint(p: Polygon, point: Vec2, epsilon: number = EPSILON): boolean {
  const n = p.length;
  if (n < 3) return false;

  if (isOnBoundary(p, point, epsilon)) return true;

  let inside = false;
  for (let i = 0, j = n - 1; i < n; j = i++) {
    const a = p[i];
    const b = p[j];
    if (a === undefined || b === undefined) continue;

    if (a.y > point.y !== b.y > point.y) {
      const x = ((b.x - a.x) * (point.y - a.y)) / (b.y - a.y) + a.x;
      if (point.x < x) inside = !inside;
    }
  }
  return inside;
}

export function isOnBoundary(p: Polygon, point: Vec2, epsilon: number = EPSILON): boolean {
  for (const e of edges(p)) {
    if (distanceToSegment(e, point) <= epsilon) return true;
  }
  return false;
}

/** Distance from a point to the polygon's boundary. Zero on the boundary. */
export function distanceToBoundary(p: Polygon, point: Vec2): number {
  let best = Infinity;
  for (const e of edges(p)) {
    const d = distanceToSegment(e, point);
    if (d < best) best = d;
  }
  return best;
}

/** True when every interior angle is at most π — tested by consistent cross-product sign. */
export function isConvex(p: Polygon, epsilon: number = EPSILON): boolean {
  const n = p.length;
  if (n < 3) return false;

  let sign = 0;
  for (let i = 0; i < n; i++) {
    const a = p[i];
    const b = p[(i + 1) % n];
    const c = p[(i + 2) % n];
    if (a === undefined || b === undefined || c === undefined) return false;

    const cross = vec.cross(vec.subtract(b, a), vec.subtract(c, b));
    if (approxZero(cross, epsilon)) continue; // collinear vertices are allowed

    const s = cross > 0 ? 1 : -1;
    if (sign === 0) {
      sign = s;
    } else if (s !== sign) {
      return false;
    }
  }
  return true;
}

export function isSimple(p: Polygon, epsilon: number = EPSILON): boolean {
  const all = [...edges(p)];
  const n = all.length;
  if (n < 4) return true;

  for (let i = 0; i < n; i++) {
    for (let j = i + 1; j < n; j++) {
      // Adjacent edges legitimately share an endpoint.
      const adjacent = j === i + 1 || (i === 0 && j === n - 1);
      const ei = all[i];
      const ej = all[j];
      if (ei === undefined || ej === undefined) continue;
      if (adjacent) continue;
      if (segmentsCross(ei, ej, epsilon)) return false;
    }
  }
  return true;
}

function segmentsCross(s: Segment, t: Segment, epsilon: number): boolean {
  const d1 = vec.subtract(s.b, s.a);
  const d2 = vec.subtract(t.b, t.a);
  const denominator = vec.cross(d1, d2);
  const scale = Math.max(1, vec.length(d1) * vec.length(d2));
  if (approxZero(denominator / scale, epsilon)) return false;

  const offset = vec.subtract(t.a, s.a);
  const u = vec.cross(offset, d2) / denominator;
  const v = vec.cross(offset, d1) / denominator;
  return u > epsilon && u < 1 - epsilon && v > epsilon && v < 1 - epsilon;
}

/**
 * Convex hull by Andrew's monotone chain: sort by x then y, then build the lower and
 * upper chains.
 *
 * `O(n log n)`, dominated by the sort, and — unlike gift wrapping — its cost does not
 * depend on the size of the hull. Collinear points are excluded from the result, so the
 * hull has no redundant vertices.
 *
 * Returned counter-clockwise, matching the orientation convention of this module.
 */
export function convexHull(points: readonly Vec2[], epsilon: number = EPSILON): Polygon {
  const n = points.length;
  if (n < 3) return [...points];

  const sorted = [...points].sort((a, b) => (a.x === b.x ? a.y - b.y : a.x - b.x));

  const cross = (o: Vec2, a: Vec2, b: Vec2): number =>
    (a.x - o.x) * (b.y - o.y) - (a.y - o.y) * (b.x - o.x);

  const build = (source: readonly Vec2[]): Vec2[] => {
    const chain: Vec2[] = [];
    for (const p of source) {
      while (chain.length >= 2) {
        const a = chain[chain.length - 2];
        const b = chain[chain.length - 1];
        if (a === undefined || b === undefined) break;
        if (cross(a, b, p) > epsilon) break;
        chain.pop();
      }
      chain.push(p);
    }
    chain.pop(); // the last point is the first of the other chain
    return chain;
  };

  const lower = build(sorted);
  const upper = build([...sorted].reverse());

  // Degenerate input (all points collinear, or duplicated) makes the two chains
  // collapse onto each other, so the concatenation can repeat a vertex. Deduplicating
  // the ring is the whole fix: the chains have already found the correct extremes, and
  // falling back to the input points here would wrongly return interior collinear
  // points as hull vertices.
  return dedupeRing([...lower, ...upper], epsilon);
}

/** Removes adjacent duplicates and a closing vertex equal to the opening one. */
function dedupeRing(ring: readonly Vec2[], epsilon: number): Vec2[] {
  const out: Vec2[] = [];
  for (const p of ring) {
    const last = out[out.length - 1];
    if (last !== undefined && vec.equals(last, p, epsilon)) continue;
    out.push(p);
  }
  while (out.length > 1) {
    const first = out[0];
    const last = out[out.length - 1];
    if (first === undefined || last === undefined || !vec.equals(first, last, epsilon)) break;
    out.pop();
  }
  return out;
}

/** A regular `n`-gon inscribed in a circle, starting at `startAngle`, counter-clockwise. */
export function regularPolygon(
  center: Vec2,
  radius: number,
  sides: number,
  startAngle = 0
): Polygon {
  const n = Math.max(3, Math.floor(sides));
  const step = (Math.PI * 2) / n;
  const result: Vec2[] = new Array<Vec2>(n);
  for (let i = 0; i < n; i++) {
    result[i] = vec.addScaled(center, vec.fromAngle(startAngle + i * step), radius);
  }
  return result;
}
